/*
 * Keeps the buy-box price in step with the Subscribe & Save selection.
 *
 * The subscription app (Appstle) renders its selling-plan radios client-side
 * and never tells the theme when one is picked. Dawn's product-info.js only
 * re-renders on variant change, and the theme's own product-subscriptions.js
 * is disabled in main-product.liquid. Without this bridge the buy box keeps
 * showing whatever price it was rendered with.
 *
 * Two jobs:
 *
 *   1. React to the customer changing the plan.
 *   2. Reconcile the first paint. The buy box is rendered with
 *      default_to_selling_plan, so Liquid guesses the app will open on the
 *      subscription. If that guess is wrong the shown price would not be the
 *      one charged at checkout, so once the widget exists its checked radio
 *      is compared against what was rendered and wins any disagreement.
 *
 * Prices are re-fetched through the Section Rendering API rather than
 * computed here, so both figures keep coming from snippets/price.liquid and
 * there is no second copy of the pricing rules in JS to drift from it.
 */
(function () {
  'use strict';

  var WIDGET_WAIT_MS = 15000;

  var container = null;
  var sectionId = null;
  var latest = 0;

  // The app injects its widget late, so resolve lazily rather than at load.
  function resolve() {
    if (container && document.contains(container)) return true;
    container = document.querySelector('[id^="price-"]');
    if (!container) return false;
    sectionId = container.id.slice('price-'.length);
    return true;
  }

  // Which plan the current markup was priced for; '' means one-time.
  function renderedPlan() {
    var priceEl = container && container.querySelector('.price');
    return priceEl ? priceEl.getAttribute('data-selling-plan') || '' : '';
  }

  /*
   * The po3 purchase-option rows are the selector the customer actually
   * uses; the app's radios only mirror them. Read the plan from the rows
   * when they exist so the price follows every click, including the
   * two-jars row whose plan the app's own change events never announced.
   */
  function po3Plan() {
    var root = document.querySelector('fieldset.po3');
    if (!root) return null;
    var option = root.querySelector('input[name="purchase_option"]:checked');
    if (!option) return null;
    if (option.value === 'single') return '';
    if (option.value === 'single-sub') {
      var cadence = root.querySelector('input[name="purchase_cadence"]:checked');
      if (cadence && cadence.value) return cadence.value;
    }
    return option.getAttribute('data-plan') || '';
  }

  function checkedPlan() {
    var fromRows = po3Plan();
    if (fromRows !== null) return fromRows;
    var radio = document.querySelector('input[name="selling_plan"]:checked');
    return radio ? radio.value || '' : null;
  }

  // The real form lives inside <product-form>; Shop Pay's installments form
  // shares the action and comes first in the document.
  function variantId() {
    var input =
      document.querySelector('product-form form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input && input.value ? input.value : null;
  }

  /*
   * Copies fresh markup and data attributes onto the live element instead of
   * replacing the node, so anything already holding a reference to it stays
   * valid. The data attributes matter: product-info.js reads
   * data-base-price-cents off the daily-cost block to recompute the figure on
   * later variant changes, and a stale one would divide from the wrong price.
   */
  function adopt(id, doc) {
    var current = document.getElementById(id);
    var fresh = doc.getElementById(id);
    if (!current || !fresh) return;
    current.innerHTML = fresh.innerHTML;
    Array.prototype.forEach.call(fresh.attributes, function (attr) {
      if (attr.name.indexOf('data-') === 0) {
        current.setAttribute(attr.name, attr.value);
      }
    });
  }

  function refresh(planId) {
    if (!resolve()) return;

    var params = new URLSearchParams({ section_id: sectionId });
    var variant = variantId();
    if (variant) params.set('variant', variant);
    if (planId) params.set('selling_plan', planId);

    // Guards against a slow response for an earlier click landing last.
    var ticket = ++latest;

    fetch(window.location.pathname + '?' + params.toString())
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        if (ticket !== latest || !resolve()) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // The .price wrapper carries price--on-sale, which is what reveals the
        // struck figure, so swapping the inner markup brings the state with it.
        adopt(container.id, doc);
        // The daily figure divides from the same price, so it has to move with
        // it or the buy box quotes a subscription price beside a one-time rate.
        adopt('Daily-Cost-' + sectionId, doc);
        // Carries the Shop Pay instalment row, which Liquid drops while a plan
        // is active — without this it would linger after switching.
        adopt('FinePrint-' + sectionId, doc);
      })
      .catch(function () {
        /* Leave the price as it stands; a stale figure beats an empty one. */
      });
  }

  function reconcile() {
    var checked = checkedPlan();
    if (checked === null) return false;        // widget not rendered yet
    if (!resolve()) return true;
    if (checked !== renderedPlan()) refresh(checked);
    return true;
  }

  /*
   * The app also rewrites the price markup on its own once its widget boots:
   * it strikes the unit price and writes a quantity total in its place, and
   * hides the block while it does. Whenever that happens, re-render the
   * block from Liquid so the buy box keeps quoting the plan price per jar,
   * the same figure the cart will use. Our own re-render leaves none of the
   * app's markers behind, so this cannot loop on itself.
   */
  function watchPriceForAppRewrites() {
    if (!resolve()) return;
    var cooling = false;
    var observer = new MutationObserver(function () {
      if (cooling || !resolve()) return;
      if (!container.querySelector('[data-appstle-price-modified], .appstle_subscription_final_price')) return;
      cooling = true;
      var plan = checkedPlan();
      refresh(plan === null ? renderedPlan() : plan);
      setTimeout(function () {
        cooling = false;
      }, 800);
    });
    observer.observe(container, { childList: true, subtree: true, attributes: true });
  }

  // Radios rendered with `checked` fire no event, so the first paint has to be
  // reconciled explicitly once the widget appears.
  function watchForWidget() {
    watchPriceForAppRewrites();
    if (reconcile()) return;

    var observer = new MutationObserver(function () {
      if (reconcile()) {
        observer.disconnect();
        clearTimeout(timer);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Stop watching if the app never loads — no widget means no plan to sync,
    // but an observer on the whole tree should not outlive its purpose.
    var timer = setTimeout(function () {
      observer.disconnect();
    }, WIDGET_WAIT_MS);
  }

  // Delegated: the radios do not exist when this script runs.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target) return;
    // A po3 row or cadence pick: re-price from the rows, a beat later so the
    // variant sync has already written the row's variant into the form.
    if (target.name === 'purchase_option' || target.name === 'purchase_cadence') {
      setTimeout(function () {
        var plan = checkedPlan();
        if (plan !== null && (plan !== renderedPlan() || !resolve())) refresh(plan);
      }, 60);
      return;
    }
    if (target.name !== 'selling_plan') return;
    // With the rows present they are the source of truth; the app's radio
    // is only a mirror and can lag behind the click.
    if (document.querySelector('fieldset.po3')) return;
    refresh(target.value);
  });

  /*
   * Dawn re-renders the price on variant change without knowing about selling
   * plans, so it comes back priced as a one-time purchase even while a plan
   * radio is still checked. Reconciling afterwards puts the plan price back.
   */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      reconcile();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForWidget);
  } else {
    watchForWidget();
  }
})();
